import { IHandBookSegment } from "../model/types";
import { IIdAndName } from "types/idAndName";

/**
 * Ищет сегмент по заданному критерию.
 * Возвращает первый найденный сегмент и его родительский сегмент.
 */
const findSegment = (segments: IHandBookSegment[], matcher: (s: IHandBookSegment | IIdAndName) => boolean) => {
  let parentSegment: IHandBookSegment | null = null;
  let segment: IIdAndName | null = null;

  // Сначала ищем среди родительских сегментов
  for (let i = 0; i < segments.length; i++) {
    const parentCandidate = segments[i];
    if (matcher(parentCandidate)) {
      parentSegment = parentCandidate;
      segment = parentCandidate;
      // Нашли родительский сегмент - возвращаем его первым
      return { segment, parentSegment: null };
    }
  }

  // Затем ищем среди дочерних сегментов
  for (let i = 0; i < segments.length; i++) {
    const parentCandidate = segments[i];
    for (let j = 0; j < parentCandidate.children.length; j++) {
      const segmentCandidate = parentCandidate.children[j];
      if (matcher(segmentCandidate)) {
        parentSegment = parentCandidate;
        segment = segmentCandidate;
        // Нашли первый подходящий дочерний сегмент, возвращаем его и его родителя
        return { segment, parentSegment };
      }
    }
  }

  return { segment, parentSegment };
};

export const findSegmentsById = (segments: IHandBookSegment[], segment_id: number) =>
  findSegment(segments, (s) => s.id === segment_id);
export const findSegmentsByName = (segments: IHandBookSegment[], segment_name: string) =>
  findSegment(segments, (s) => s.name === segment_name);

export interface ICreateLinkedSegments {
  segment: Partial<{
    id: number | null;
    name: string | null;
  }>;
  parentSegment: Partial<{
    id: number | null;
    name: string | null;
  }>;
  expenditureId: number;
}

/** Объединяет сегменты с одинаковой вложенностью по наименованию, но которые ещё не созданы. С дедупликацией по существующим сегментам */
export const createLinkedSegments = (
  segments: IHandBookSegment[],
  rawLinkedSegments: Array<ICreateLinkedSegments>,
  createSegment: (data: { name: string; parent?: number }) => Promise<IHandBookSegment | void>
): Promise<Record<number, ICreateLinkedSegments>> => {
  // Список сегментов, дополненный новыми созданными сегментами в этом запуске
  let updatedSegments = [...segments];

  // Map для отслеживания созданных сегментов по имени и родителю
  const createdSegments: { [nameAndParent: string]: number } = {};
  // Map для отслеживания созданных родительских сегментов по имени
  const parentIdMap: { [parentName: string]: number } = {};
  // Map для хранения вновь созданных сегментов для дедупликации в пакетном создании
  const newlyCreatedSegments: { [key: string]: IHandBookSegment | IIdAndName } = {};

  // Создает ключ для хеширования имени сегмента и его родителя
  const getSegmentKey = (segmentName: string, parentId?: number) => `${segmentName}:${parentId || "root"}`;

  // Ключ для родительских сегментов
  const getParentKey = (parentName: string) => `parent:${parentName}`;

  // Обработка дочерних сегментов
  const processSegment = async (
    rawSegment: ICreateLinkedSegments,
    parentSegmentId?: number
  ): Promise<IHandBookSegment | IIdAndName | null> => {
    const { segment } = rawSegment;

    // Строгое сравнение с null для удаления сегмента
    if (segment.name === null) {
      return null;
    }

    // Если указан ID сегмента, используем его
    if (segment.id) {
      return findSegmentsById(segments, segment.id).segment;
    }

    if (segment.name) {
      // Создаем уникальный ключ для пары сегмент-родитель
      const segmentKey = getSegmentKey(segment.name, parentSegmentId);

      // Проверяем, есть ли уже созданный сегмент с таким же именем и родителем в этом батче
      if (newlyCreatedSegments[segmentKey]) {
        return newlyCreatedSegments[segmentKey];
      }

      // Проверяем, есть ли уже созданный сегмент с таким же именем и родителем в базе
      if (createdSegments[segmentKey]) {
        const foundSegment = findSegmentsById(updatedSegments, createdSegments[segmentKey]).segment;
        if (foundSegment) {
          return foundSegment;
        }
      }

      // Если указан родительский сегмент, ищем дочерний сегмент ТОЛЬКО у этого родителя
      if (parentSegmentId) {
        // Сначала ищем среди уже существующих сегментов
        const parent = updatedSegments.find((s) => s.id === parentSegmentId);
        if (parent) {
          // Ищем дочерний сегмент с таким именем У ЭТОГО родителя
          const childSegment = parent.children.find((child) => child.name === segment.name);
          if (childSegment) {
            // Нашли у указанного родителя, используем его
            createdSegments[segmentKey] = childSegment.id;
            return childSegment;
          }
          // Не нашли у этого родителя - нужно создать новый
          // НЕ ИЩЕМ у других родителей - это ключевой момент для решения проблемы
        }
      } else {
        // Родительский сегмент не указан - это сегмент верхнего уровня
        // Ищем сегмент верхнего уровня с таким именем
        const existingRootSegment = updatedSegments.find((s) => s.name === segment.name);
        if (existingRootSegment) {
          // Нашли сегмент верхнего уровня, используем его
          createdSegments[segmentKey] = existingRootSegment.id;
          return existingRootSegment;
        }
        // Не нашли родительский сегмент - создаем новый
      }

      // Создаем новый сегмент
      const newSegment = await createSegment({ name: segment.name, parent: parentSegmentId });
      if (newSegment) {
        // Сохраняем в кэше созданных сегментов в этом батче
        newlyCreatedSegments[segmentKey] = newSegment;

        // Добавляем в список обновленных сегментов
        if (parentSegmentId) {
          // Если это дочерний сегмент, добавляем его к родительскому
          const parentIndex = updatedSegments.findIndex((s) => s.id === parentSegmentId);
          if (parentIndex !== -1) {
            const parent = { ...updatedSegments[parentIndex] };
            parent.children = [...parent.children, newSegment as IIdAndName];
            updatedSegments[parentIndex] = parent;
          }
        } else {
          // Если это родительский сегмент, добавляем его в список
          updatedSegments.push(newSegment as IHandBookSegment);
        }

        createdSegments[segmentKey] = newSegment.id;
        return newSegment;
      }
    }

    // Ситуация, когда segment.name не null, но нет ни id, ни name - выбрасываем ошибку
    throw new Error("Segment должен иметь id, name или быть null.");
  };

  return rawLinkedSegments.reduce(async (promise, rawSegment) => {
    const resolvedResults = await promise;
    const { parentSegment, segment } = rawSegment;
    let processedParentSegment: IHandBookSegment | IIdAndName | null = null;
    let parentSegmentId: number | undefined;

    // Обработка родительского сегмента
    if (parentSegment.name === null) {
      // Если родительский сегмент null, значит его нет
      processedParentSegment = null;
    } else if (parentSegment.id) {
      // Если есть ID родительского сегмента, используем его
      processedParentSegment = segments.find((s) => s.id === parentSegment.id) || null;
      parentSegmentId = processedParentSegment?.id;
    } else if (parentSegment.name) {
      // Когда дано имя родительского сегмента
      const parentKey = getParentKey(parentSegment.name);

      // Проверяем, был ли уже создан родитель с таким именем в этом раунде создания
      if (parentIdMap[parentKey]) {
        parentSegmentId = parentIdMap[parentKey];
        processedParentSegment = segments.find((s) => s.id === parentSegmentId)!;
      } else {
        // Ищем среди родительских сегментов
        const existingParent = segments.find((s) => s.name === parentSegment.name);
        if (existingParent) {
          // Нашли существующий родительский сегмент
          parentIdMap[parentKey] = existingParent.id;
          parentSegmentId = existingParent.id;
          processedParentSegment = existingParent;
        } else {
          // Создаем новый родительский сегмент
          const newParentSegment = await createSegment({ name: parentSegment.name });
          if (newParentSegment) {
            parentIdMap[parentKey] = newParentSegment.id;
            parentSegmentId = newParentSegment.id;
            processedParentSegment = newParentSegment;
          }
        }
      }
    }

    // Проверка на недопустимую ситуацию: указан дочерний сегмент, а родительский - null
    if (segment.name !== null && parentSegment.name === null && !parentSegment.id && !segment.id) {
      throw new Error("Недопустимая ситуация: указан дочерний сегмент, а родительский - null.");
    }

    // Не нужно проверять сегмент на соответствие родителю здесь,
    // так как эта проверка уже выполняется в processSegment

    const createdSegment = await processSegment(rawSegment, parentSegmentId);

    resolvedResults[rawSegment.expenditureId] = {
      ...rawSegment,
      segment: createdSegment ? { id: createdSegment.id, name: createdSegment.name } : { id: null, name: null },
      parentSegment: processedParentSegment
        ? { id: processedParentSegment.id, name: processedParentSegment.name }
        : { id: null, name: null },
    };

    return resolvedResults;
  }, Promise.resolve({}) as Promise<Record<number, ICreateLinkedSegments>>);
};

export const transformFormValuesToLinkedSegment = (
  {
    segment,
    parent_segment,
  }: {
    segment?: number | string | null;
    parent_segment?: number | string | null;
  },
  expenditureId: number
): ICreateLinkedSegments => {
  return {
    segment: typeof segment === "number" ? { id: segment } : { name: segment?.trim() || null },
    parentSegment:
      typeof parent_segment === "number" ? { id: parent_segment } : { name: parent_segment?.trim() || null },
    expenditureId,
  };
};
